import { isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  PLATFORM_ID,
  effect,
  DOCUMENT,
  inject,
  OnInit,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Layout } from 'src/app/models/layout/layout.model';
import { LayoutService } from 'src/app/services/layout.service';
import { HeaderLogoComponent } from './header-logo/header-logo.component';
import { BookingAuthService } from 'src/app/services/booking-auth.service';
import { BookingAuthDialogComponent } from '../booking/booking-auth-dialog/booking-auth-dialog.component';
import { SettingsService } from 'src/app/services/settings.service';

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgClass,
    NgTemplateOutlet,
    HeaderLogoComponent,
    BookingAuthDialogComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  layoutService = inject(LayoutService);
  bookingAuthService = inject(BookingAuthService);
  platformId = inject(PLATFORM_ID);
  document = inject(DOCUMENT);
  router = inject(Router);
  settingsService = inject(SettingsService);

  layout?: Layout;

  apiDomain = environment.apiDomain;
  showMenu = false;
  showAuthDialog = false;

  styleString = '';

  headerHeight = 50;

  constructor() {
    effect(() => {
      this.layout = this.layoutService.layout();
      // this.layout.HeaderLayout.Type = 2;
      // this.layout.HeaderLayout.ShowLogo = true;

      this.checkForFixedHeader();

      if (this.layout?.HeaderLayout.Height) {
        this.headerHeight = this.layout.HeaderLayout.Height.replace(
          'px',
          '',
        ) as unknown as number;
      }

      if (this.layout?.HeaderLayout?.Links) {
        this.layout.HeaderLayout.Links.forEach((link) => {
          if (link.Url.substring(0, 4) === 'http') {
            link.ExternalLink = true;
          } else if (link.Url.substring(0, 1) !== '/') {
            link.Url = '/' + link.Url;
          }

          if (link.Children && link.Children.length > 0) {
            link.Children.forEach((childLink) => {
              if (childLink.Url.substring(0, 4) === 'http') {
                childLink.ExternalLink = true;
              } else if (childLink.Url.substring(0, 1) !== '/') {
                childLink.Url = '/' + childLink.Url;
              }
            });
          }
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.layout?.HeaderLayout.Styles) {
      this.styleString = this.layout.HeaderLayout.Styles.join('; ');
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkForFixedHeader();
  }

  checkForFixedHeader() {
    if (isPlatformBrowser(this.platformId)) {
      const styles = this.layout?.HeaderLayout;
      if (!styles) {
        return;
      }

      const responsive = this.layout?.ResponsiveLayout;
      const width =
        window.innerWidth ||
        this.document.documentElement.clientWidth ||
        this.document.body.clientWidth;
      const isMobile = width <= (responsive?.MobileBreakpoint ?? 578);
      const isTablet =
        width > (responsive?.MobileBreakpoint ?? 578) &&
        width <= (responsive?.TabletBreakpoint ?? 998);
      const isDesktop = !isMobile && !isTablet;
      const body = this.document.querySelector('body');

      body?.classList.remove('header-fixed', 'header-sticky');

      if (
        (styles.FixedDesktop && isDesktop) ||
        (styles.FixedTablet && isTablet) ||
        (styles.FixedMobile && isMobile)
      ) {
        body?.classList.add('header-fixed');
      } else if (
        (styles.StickyDesktop && isDesktop) ||
        (styles.StickyTablet && isTablet) ||
        (styles.StickyMobile && isMobile)
      ) {
        body?.classList.add('header-sticky');
      }

      var screenHeight =
        window.innerHeight ||
        this.document.documentElement.clientHeight ||
        this.document.body.clientHeight;

      if (styles.FixedDesktop && isDesktop && screenHeight > 998) {
        body?.classList.add('header-fixed');
      } else if (styles.FixedTablet && width <= 998) {
        body?.classList.add('header-fixed');
      }
    }
  }

  showMenuToggle() {
    this.showMenu = !this.showMenu;
    if (this.showMenu) {
      this.document.querySelector('body')?.classList.add('modal-open');
    } else {
      this.document.querySelector('body')?.classList.remove('modal-open');
    }
  }

  hideMenu() {
    this.showMenu = false;
    this.document.querySelector('body')?.classList.remove('modal-open');
  }

  handleBookingAuthClick(): void {
    this.hideMenu();

    if (this.bookingAuthService.isLoadingProfile()) {
      return;
    }

    if (this.bookingAuthService.isLoggedIn()) {
      void this.router.navigate(['/profile']);
      return;
    }

    this.showAuthDialog = true;
  }

  closeBookingAuthDialog(): void {
    this.showAuthDialog = false;
  }

  usesAuthenticationModule(): boolean {
    return Boolean(
      this.settingsService.setting().Modules?.Authentication ?? false,
    );
  }

  isMobileMenuRight(): boolean {
    return (
      this.layout?.HeaderLayout?.MobileMenuPosition?.toLowerCase() === 'right'
    );
  }

  getLeftLinks() {
    const links = this.layout?.HeaderLayout?.Links ?? [];
    const splitIndex = Math.ceil(links.length / 2);
    return links.slice(0, splitIndex);
  }

  getRightLinks() {
    const links = this.layout?.HeaderLayout?.Links ?? [];
    const splitIndex = Math.ceil(links.length / 2);
    return links.slice(splitIndex);
  }
}
